import {type KeyboardEvent, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext, UseFormGetValues} from 'react-hook-form';
import {TFunction} from 'i18next';
import {
  PATTERNS,
  mergeRefs,
  getPhoneNumberString,
  isActiveFormField,
  POLICE_CODES,
  COUNTRY_CODES,
  QUERY_CACHE_KEYS,
  GUEST_FORM_NAMES,
  SelectOption,
} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useGuestGroup, useFirstGuestMustBeAdult} from 'hooks';
import {useSchema} from 'context/guest';
import {useVirtualKeyboard} from 'components/GuestFormFields/VirtualKeyboard';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {i18n} from '../../configs/i18n/i18n';
import {
  EXTRA_FORM_NAMES,
  FormTypes,
  getIsLeaderGuest,
  IV_FIELDS,
  SIGNATURE_FIELDS,
} from 'utils/guestForm';
import {createGetRequiredOrOptionalFieldLabel} from 'utils/getRequiredOrOptionalFieldLabel';
import {getIsSelectFieldType, getResidenceCityQueryKey} from './utils/common';
import {
  handleChangeResidenceCountry,
  handleChangeResidenceProvince,
} from './utils/handlers';
import {sortFormNamesByOrder} from './utils/sortFormNamesByOrder';
import {type FormFieldsMapper} from './types';
import {
  getDocNumberPattern,
  maxBirthPlaceAddressLength,
  maxLength,
  maxNamesInputLength,
  minLength,
  validateChipCard,
  validateBirthDate,
  validateDocNumberByDocumentType,
  validateExpirationDate,
  validateIssueDate,
  validateResidencePhoneNumber,
  validateGuestMustBeAdult,
  validatePostalCode,
  validateDocNumberUniqueness,
  validateSelectedDocumentType,
  getDocumentSupportNumberInvalidMessage,
} from 'pages/AddPersonalDataForm/utils';
import Datepicker from 'components/common/Datepicker';
import PhoneInput from 'components/common/PhoneInput';
import AdaptiveQueriedInfiniteScrollSelect from 'components/common/QueriedInfiniteScrollSelect';
import {GuestEmailField} from 'components/GuestFormFields';
import {Select, InputController as Input} from '@guestapp/old-ui-kit';
import {FormFieldWrapper} from 'styled/common';
import {API, Validators} from '@chekin/api';
import {CountriesSelect} from '@chekin/shared';

// TODO Feature flag
const isNewLabel = false;

type GetEmailTooltipProps = {
  policeType?: string;
  isLeaderForm?: boolean;
};
const getEmailTooltip = ({policeType, isLeaderForm}: GetEmailTooltipProps) => {
  if (policeType === POLICE_CODES.germanyAVS) {
    return isLeaderForm
      ? i18n.t('avs_email_tooltip.leader')
      : i18n.t('avs_email_tooltip.rest');
  }
};

const getNameValidationErrorText = (country: string, policeType = '') => {
  if (
    country === COUNTRY_CODES.spain &&
    [POLICE_CODES.hospedajes, POLICE_CODES.mossos, POLICE_CODES.ertzaintza].includes(
      policeType,
    )
  ) {
    return i18n.t('guest_names_validation_text.ses_and_mossos_spain');
  } else if (
    [COUNTRY_CODES.spain, COUNTRY_CODES.uae, COUNTRY_CODES.czech].includes(country)
  ) {
    return i18n.t('guest_names_validation_text.only_letters');
  } else {
    return i18n.t('guest_names_validation_text.rest');
  }
};

const filterGenderOptions = (options?: SelectOption[]): SelectOption[] | undefined => {
  return options?.filter(opt => opt?.value !== 'N');
};

const getNoOptionsMessageForDocTypeField =
  (getValues: UseFormGetValues<FormTypes>, t: TFunction) => () => {
    const isNationalityFilled = Boolean(getValues()[GUEST_FORM_NAMES.nationality]?.value);

    return !isNationalityFilled ? t('please_fill_nationality_before') : undefined;
  };

const getPhoneFieldLabel = (defaultLabel: string, t: TFunction, policeType?: string) => {
  if (policeType && [POLICE_CODES.uhh, POLICE_CODES.uhh2].includes(policeType)) {
    return t('residence_country_phone');
  }

  return defaultLabel;
};

const getRecipientCodeLabel = (defaultLabel: string, recipientCodeType?: string) => {
  return recipientCodeType || defaultLabel;
};

type UseDefaultFieldsMapperProps = {
  housingCountry: string;
  guestId?: string;
  isDisabledField?: boolean;
};
export const useDefaultFieldsMapper = ({
  guestId,
  isDisabledField,
  housingCountry,
}: UseDefaultFieldsMapperProps) => {
  const virtualKeyboard = useVirtualKeyboard();
  const {datepickerRefs, phoneInputRefs, countrySelectInputsRegister} = virtualKeyboard;
  const {formFields, setHasPhone, isGuestSchemaLoading} = useSchema();
  const {guestGroup, isLeaderGuest} = useGuestGroup();
  const {firstGuestMustBeAdult: shouldFirstGuestBeAdult, guestMustBeAdult} =
    useFirstGuestMustBeAdult();

  const {register, control, formState, setFocus, setValue, getValues, watch} =
    useFormContext<FormTypes>();
  const {t} = useTranslation();
  const getRequiredOrOptionalFieldLabel = useMemo(
    () => createGetRequiredOrOptionalFieldLabel(t),
    [t],
  );

  const defaultSecondPhoneDetails = getValues()?.[EXTRA_FORM_NAMES.secondPhoneDetails];

  const {data: reservation, housing} = useReservation();
  const layoutMode = useLayoutMode();

  const isLeaderForm = getIsLeaderGuest() || isLeaderGuest;

  const errors = useMemo(() => {
    return formState.errors;
  }, [formState]);
  const policeType = housing?.police_account_type;
  const isPoliceActivated = housing?.is_police_registration_enabled;

  const names = useMemo(() => {
    const names: GUEST_FORM_NAMES[] = [];
    if (Object.values(formFields).length > 0) {
      Object.entries(formFields).forEach(([name, value]) => {
        if (
          value.is_active &&
          !IV_FIELDS.includes(name) &&
          !SIGNATURE_FIELDS.includes(name as GUEST_FORM_NAMES)
        ) {
          names.push(name as GUEST_FORM_NAMES);
        }
      });
      return sortFormNamesByOrder(names, formFields);
    } else {
      return [];
    }
  }, [formFields]);

  const getFormFieldDestinations = useMemo(() => {
    return names.map((formName, index) => {
      const destination = index < names.length - 1 ? names[index + 1] : '';
      return {
        state: formName,
        destination,
      };
    });
  }, [names]);

  const getNextField = useCallback(
    (nextFieldName: GUEST_FORM_NAMES) => {
      return getFormFieldDestinations.find(item => item.state === nextFieldName);
    },
    [getFormFieldDestinations],
  );

  const handleKeyDown = useCallback(
    (nextFieldName: GUEST_FORM_NAMES) => (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const next = getNextField(nextFieldName);
        next && setFocus(next?.destination);
      }
    },
    [setFocus, getNextField],
  );

  const documentType = watch(GUEST_FORM_NAMES.document_type)?.value;
  const residenceCountry = watch(GUEST_FORM_NAMES.residence_country)?.value;
  const residenceProvince = watch(GUEST_FORM_NAMES.residence_province)?.value;
  const arrivedFromDistrict = watch(GUEST_FORM_NAMES.arrived_from_district)?.value;
  const nextDestinationDistrict = watch(
    GUEST_FORM_NAMES.next_destination_district,
  )?.value;
  const birthPlaceCountry = watch(GUEST_FORM_NAMES.birth_place_country)?.value;
  const recipientCodeType = watch(GUEST_FORM_NAMES.recipient_code_type)?.label;

  return useMemo<FormFieldsMapper>(
    () => ({
      [GUEST_FORM_NAMES.name]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel ? formFields[GUEST_FORM_NAMES.name]?.label : t('name'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.name, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.name]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            autoComplete={layoutMode === 'kiosko' ? 'off' : 'on'}
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.name)}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.name)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.name)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel ? formFields[GUEST_FORM_NAMES.surname]?.label : t('surname'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            autoComplete={layoutMode === 'kiosko' ? 'off' : 'on'}
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.surname)}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.surname)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.surname)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.second_surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.second_surname]?.label
                : t('second_surname'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.second_surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.second_surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.second_surname)}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.second_surname)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.second_surname)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.nationality]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.nationality}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.nationality);

              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.nationality]?.label
                      : t('nationality'),
                    required,
                  )}
                  disabled={isDisabledField}
                  onKeyDown={handleKeyDown(GUEST_FORM_NAMES.nationality)}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.nationality)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur('nationality');
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_place_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.birth_place_country}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.birth_place_country);
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.birth_place_country]?.label
                      : t('birth_place_country'),
                    required,
                  )}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_country)}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.birth_place_country)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(GUEST_FORM_NAMES.birth_place_country);
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_place_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.birth_place_city]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.birth_place_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(
                      isNewLabel
                        ? formFields[GUEST_FORM_NAMES.birth_place_city]?.label
                        : t('city_of_birth'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    queryKey={[QUERY_CACHE_KEYS.paginatedCitiesList]}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      API.locations.getCitiesList({
                        key,
                        page,
                        searchQuery,
                        countryCode: birthPlaceCountry || '',
                      })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    value={value as SelectOption}
                    error={error?.message}
                    openMenuOnFocus={false}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.birth_place_city]?.label
                  : t('city_of_birth'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.birth_place_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[GUEST_FORM_NAMES.birth_place_city]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_city)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.birth_place_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.birth_place_address]?.label
                : t('birth_place_address'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.birth_place_address, {
              required,
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              maxLength: {
                value: maxBirthPlaceAddressLength,
                message: t('max_length', {length: maxBirthPlaceAddressLength}),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.birth_place_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_address)}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.birth_place_address)
            }
            onBlur={() =>
              virtualKeyboard.handleBlur(GUEST_FORM_NAMES.birth_place_address)
            }
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.tax_exemption]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.tax_exemption]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.tax_exemption}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.tax_exemption]?.label
                    : t('tax_exemption'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                loading={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.tax_exemption)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.email]: ({required, format}) => (
        <FormFieldWrapper>
          <GuestEmailField
            name={GUEST_FORM_NAMES.email}
            required={required}
            format={format}
            tooltipContent={getEmailTooltip({policeType, isLeaderForm})}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel ? formFields[GUEST_FORM_NAMES.email]?.label : t('email'),
              required,
            )}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.email)}
            disabled={isDisabledField}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.email)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.email)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.phone]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.phone}
            rules={{
              required,
              validate: {
                length: validateResidencePhoneNumber(t, policeType),
                full: Validators.phoneValidateService,
              },
            }}
            control={control}
            render={({field: {onBlur, onChange, ...restField}, fieldState: {error}}) => (
              <PhoneInput
                label={getRequiredOrOptionalFieldLabel(
                  getPhoneFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.phone]?.label
                      : t('phone_number'),
                    t,
                    policeType,
                  ),
                  required,
                )}
                error={error?.message}
                onBlur={() => {
                  const phoneValue = getValues()[GUEST_FORM_NAMES.phone];
                  const hasNumber = !!getPhoneNumberString(phoneValue);
                  setHasPhone?.(hasNumber);
                  onBlur();
                }}
                onChange={(_, __, details) => onChange(details)}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.phone)}
                virtualKeyboardRef={phoneInputRefs?.[GUEST_FORM_NAMES.phone]}
                onFieldFocus={subfield =>
                  virtualKeyboard.handleFocus(`${GUEST_FORM_NAMES.phone}_${subfield}`)
                }
                onFieldBlur={subfield =>
                  virtualKeyboard.handleBlur(`${GUEST_FORM_NAMES.phone}_${subfield}`)
                }
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.second_phone]: ({required, format}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.second_phone}
            rules={{
              required,
              pattern: {
                value: format,
                message: t('invalid_phone_number'),
              },
              maxLength: {
                value: 16,
                message: t('invalid_phone_number'),
              },
              minLength: {
                value: 12,
                message: t('invalid_phone_number'),
              },
            }}
            control={control}
            render={({field: {onBlur: _, ...restField}, fieldState: {error}}) => (
              <PhoneInput
                defaultCode="+971"
                defaultInputValue={defaultSecondPhoneDetails?.number}
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.second_phone]?.label
                    : t('uae_phone_number'),
                  required,
                )}
                error={error?.message}
                disabled={isDisabledField}
                tooltipContent={t('uae_phone_number_tooltip')}
                codeReadOnly
                virtualKeyboardRef={phoneInputRefs?.[GUEST_FORM_NAMES.second_phone]}
                onFieldFocus={subfield =>
                  virtualKeyboard.handleFocus(
                    `${GUEST_FORM_NAMES.second_phone}_${subfield}`,
                  )
                }
                onFieldBlur={subfield =>
                  virtualKeyboard.handleBlur(
                    `${GUEST_FORM_NAMES.second_phone}_${subfield}`,
                  )
                }
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.purpose_of_stay]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.purpose_of_stay]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.purpose_of_stay}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.purpose_of_stay]?.label
                    : t('purpose_of_stay'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                loading={isGuestSchemaLoading}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.purpose_of_stay)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.document_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_type}
            rules={{
              required,
              validate: {
                validOption: validateSelectedDocumentType(
                  formFields?.[GUEST_FORM_NAMES.document_type]?.options,
                ),
              },
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.document_type]?.label
                    : t('document_type'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.document_type)}
                noOptionsMessage={getNoOptionsMessageForDocTypeField(getValues, t)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_number]: ({required}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.document_number]?.label
                : t('document_number'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.document_number, {
              required,
              pattern: {
                value: getDocNumberPattern(housingCountry, isPoliceActivated),
                message: t('only_basic_letters_are_allowed'),
              },
              maxLength: {
                value: maxLength,
                message: t('max_length', {length: maxLength}),
              },
              minLength: {
                value: minLength,
                message: t('min_length', {length: minLength}),
              },
              validate: {
                byDocumentType: value =>
                  validateDocNumberByDocumentType(value, documentType),
                uniqueness: validateDocNumberUniqueness(guestGroup, guestId),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.document_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.document_number)}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.document_number)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.document_number)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_issue_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_issue_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;
                const birthDate = getValues()[GUEST_FORM_NAMES.birth_date];
                const expirationDate =
                  getValues()[GUEST_FORM_NAMES.document_expiration_date];
                return validateIssueDate(value, birthDate, expirationDate, documentType);
              },
            }}
            control={control}
            render={({field: {ref: controllerRef, ...field}, fieldState: {error}}) => (
              <Datepicker
                withTooltip
                tooltipContent={t('date_when_the_doc_created')}
                error={error?.message}
                disabled={isDisabledField}
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.document_issue_date]?.label
                    : t('date_of_issue'),
                  required,
                )}
                onFieldFocus={subfield =>
                  virtualKeyboard.handleFocus(
                    `${GUEST_FORM_NAMES.document_issue_date}_${subfield}`,
                  )
                }
                onFieldBlur={subfield =>
                  virtualKeyboard.handleBlur(
                    `${GUEST_FORM_NAMES.document_issue_date}_${subfield}`,
                  )
                }
                {...field}
                ref={mergeRefs(
                  datepickerRefs[GUEST_FORM_NAMES.document_issue_date],
                  controllerRef,
                )}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expiration_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_expiration_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;

                const birthDate = getValues()[GUEST_FORM_NAMES.birth_date];
                const issueDate = getValues()[GUEST_FORM_NAMES.document_issue_date];
                return validateExpirationDate({
                  expirationDate: value,
                  birthDate,
                  issueDate,
                  checkInDate: reservation?.check_in_date,
                });
              },
            }}
            control={control}
            render={({field: {ref: controllerRef, ...field}, fieldState: {error}}) => (
              <Datepicker
                error={error?.message}
                disabled={isDisabledField}
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.document_expiration_date]?.label
                    : t('document_expiration_date'),
                  required,
                )}
                onFieldFocus={subfield =>
                  virtualKeyboard.handleFocus(
                    `${GUEST_FORM_NAMES.document_expiration_date}_${subfield}`,
                  )
                }
                onFieldBlur={subfield =>
                  virtualKeyboard.handleBlur(
                    `${GUEST_FORM_NAMES.document_expiration_date}_${subfield}`,
                  )
                }
                {...field}
                ref={mergeRefs(
                  datepickerRefs[GUEST_FORM_NAMES.document_expiration_date],
                  controllerRef,
                )}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.birth_date}
            rules={{
              required,
              validate: {
                validateGuestMustBeAdult: value => {
                  if (shouldFirstGuestBeAdult || guestMustBeAdult) {
                    const validationType = getIsLeaderGuest() ? 'leader' : 'common';
                    const isSpainHousing = housingCountry === COUNTRY_CODES.spain;

                    return validateGuestMustBeAdult({
                      birthDate: value,
                      validationType,
                      isSpainHousing,
                    });
                  }
                  return true;
                },
                common: value => {
                  const isDocumentIssueDateActive = isActiveFormField(
                    formFields[GUEST_FORM_NAMES.document_issue_date],
                  );
                  const docDateOfIssue =
                    getValues()[GUEST_FORM_NAMES.document_issue_date];
                  const compareDates =
                    isDocumentIssueDateActive && Boolean(docDateOfIssue);

                  if (!value || !compareDates) {
                    return true;
                  }

                  return validateBirthDate(value, docDateOfIssue);
                },
              },
            }}
            control={control}
            render={({field: {ref: controllerRef, ...field}, fieldState: {error}}) => {
              return (
                <Datepicker
                  error={error?.message}
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.birth_date]?.label
                      : t('birth_date'),
                    required,
                  )}
                  disabled={isDisabledField}
                  onHandleKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_date)}
                  onFieldFocus={subfield =>
                    virtualKeyboard.handleFocus(
                      `${GUEST_FORM_NAMES.birth_date}_${subfield}`,
                    )
                  }
                  onFieldBlur={subfield =>
                    virtualKeyboard.handleBlur(
                      `${GUEST_FORM_NAMES.birth_date}_${subfield}`,
                    )
                  }
                  {...field}
                  ref={mergeRefs(
                    datepickerRefs[GUEST_FORM_NAMES.birth_date],
                    controllerRef,
                  )}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.gender]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.gender]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.gender}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel ? formFields[GUEST_FORM_NAMES.gender]?.label : t('sex'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={filterGenderOptions(options)}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.gender)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.citizenship]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.citizenship}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.citizenship);
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.citizenship]?.label
                      : t('citizenship'),
                    required,
                  )}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.citizenship)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(GUEST_FORM_NAMES.citizenship);
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expedition_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_expedition_country}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(
                GUEST_FORM_NAMES.document_expedition_country,
              );
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.document_expedition_country]?.label
                      : t('country_of_issue'),
                    required,
                  )}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard?.handleFocus(
                      GUEST_FORM_NAMES.document_expedition_country,
                    )
                  }
                  {...field}
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(
                      GUEST_FORM_NAMES.document_expedition_country,
                    );
                  }}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expedition_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.document_expedition_city]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.document_expedition_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(
                      isNewLabel
                        ? formFields[GUEST_FORM_NAMES.document_expedition_city]?.label
                        : t('city_of_issue'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    queryKey={QUERY_CACHE_KEYS.paginatedCitiesList}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      API.locations.getCitiesList({
                        key,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                      })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    value={value as SelectOption}
                    error={error?.message}
                    openMenuOnFocus={false}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.document_expedition_city]?.label
                  : t('city_of_issue'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.document_expedition_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.full_tourist_tax]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.full_tourist_tax, {
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.full_tourist_tax]?.label
                : t('full_tourist_tax'),
              required,
            )}
            inputMode="numeric"
            error={errors[GUEST_FORM_NAMES.full_tourist_tax]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.full_tourist_tax)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.full_tourist_tax)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_country}
            rules={{required}}
            control={control}
            render={({field: {onChange, onBlur, ...field}, fieldState: {error}}) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.residence_country);
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.residence_country]?.label
                      : t('residence_country'),
                    required,
                  )}
                  onChange={(newValue, actionMeta) => {
                    handleChangeResidenceCountry({
                      newValue,
                      formFields,
                      currentValue: residenceCountry,
                      onReset: name => setValue(name, null),
                    });

                    handleChange?.(() => onChange(newValue, actionMeta))(newValue);
                  }}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.residence_country)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(GUEST_FORM_NAMES.residence_country);
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_community]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.residence_community]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_community}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.residence_community]?.label
                    : t('residence_state'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_province]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_province}
            rules={{required}}
            control={control}
            render={({field: {onChange, ...field}, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.residence_province]?.label
                    : t('residence_province'),
                  required,
                )}
                onChange={(newValue, actionMeta) => {
                  handleChangeResidenceProvince({
                    newValue,
                    formFields,
                    currentValue: residenceProvince,
                    onReset: name => setValue(name, null),
                  });
                  onChange(newValue, actionMeta);
                }}
                placeholder={`${t('select')}...`}
                queryKey={[QUERY_CACHE_KEYS.paginatedProvincesList, housingCountry]}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  API.locations.getProvincesList({
                    key,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.residence_city]?.type,
        );

        if (isSelectField) {
          const isSpain = residenceCountry === COUNTRY_CODES.spain;
          const url = formFields?.[GUEST_FORM_NAMES.residence_city]?.options_url;
          const label = isSpain
            ? t('city')
            : isNewLabel
            ? formFields[GUEST_FORM_NAMES.residence_city]?.label
            : t('residence_city');

          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.residence_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...field}, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(label, required)}
                    placeholder={`${t('select')}...`}
                    queryKey={getResidenceCityQueryKey({residenceCountry, url})}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      isSpain
                        ? url
                          ? API.locations.getLocationsByExternalUrl({
                              url,
                              key,
                              page,
                              searchQuery,
                            })
                          : API.locations.getSpanishResidenceCitiesList({
                              key,
                              page,
                              searchQuery,
                            })
                        : API.locations.getCitiesList({
                            key,
                            page,
                            searchQuery,
                            countryCode: residenceCountry || '',
                          })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    error={error?.message}
                    openMenuOnFocus={false}
                    value={value as SelectOption}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }

        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.residence_city]?.label
                  : t('residence_city'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.residence_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[GUEST_FORM_NAMES.residence_city]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.arrived_from_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.arrived_from_country}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.arrived_from_country);
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.arrived_from_country]?.label
                      : t('arrived_from_country'),
                    required,
                  )}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.arrived_from_country)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(GUEST_FORM_NAMES.arrived_from_country);
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.arrived_from_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.arrived_from_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.arrived_from_district]?.label
                    : t('arrived_from_district'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                queryKey={QUERY_CACHE_KEYS.paginatedDistrictsList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  API.locations.getDistrictsList({
                    key,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.arrived_from_municipality]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.arrived_from_municipality]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.arrived_from_municipality}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(
                      isNewLabel
                        ? formFields[GUEST_FORM_NAMES.arrived_from_municipality]?.label
                        : t('arrived_from_municipality'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    queryKey={[
                      QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                      arrivedFromDistrict,
                    ]}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      API.locations.getMunicipalitiesList({
                        key,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                        district: arrivedFromDistrict,
                      })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    value={value as SelectOption}
                    error={error?.message}
                    openMenuOnFocus={false}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.arrived_from_municipality]?.label
                  : t('arrived_from_municipality'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.arrived_from_municipality, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[GUEST_FORM_NAMES.arrived_from_municipality]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.arrived_from_municipality)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.next_destination_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.next_destination_country}
            rules={{required}}
            control={control}
            render={({
              field: {onChange: fieldOnChange, onBlur, ...field},
              fieldState: {error},
            }) => {
              const {
                onChange: handleChange,
                onInputChange,
                value: selectInputValue,
              } = countrySelectInputsRegister(GUEST_FORM_NAMES.next_destination_country);
              return (
                <CountriesSelect
                  label={getRequiredOrOptionalFieldLabel(
                    isNewLabel
                      ? formFields[GUEST_FORM_NAMES.next_destination_country]?.label
                      : t('next_destination_country'),
                    required,
                  )}
                  placeholder={`${t('select')}...`}
                  disabled={isDisabledField}
                  error={error?.message}
                  inputValue={selectInputValue}
                  onInputChange={onInputChange}
                  onChange={option => {
                    handleChange?.(() => fieldOnChange(option))(option);
                  }}
                  onFocus={() =>
                    virtualKeyboard.handleFocus(GUEST_FORM_NAMES.next_destination_country)
                  }
                  onBlur={() => {
                    onBlur();
                    virtualKeyboard.handleBlur(GUEST_FORM_NAMES.next_destination_country);
                  }}
                  {...field}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.next_destination_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <AdaptiveQueriedInfiniteScrollSelect
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.next_destination_district]?.label
                    : t('next_destination_district'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                queryKey={QUERY_CACHE_KEYS.paginatedDistrictsList}
                fetcher={(key: string, page: number, searchQuery: string) =>
                  API.locations.getDistrictsList({
                    key,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                blockQuery={isDisabledField}
                disabled={isDisabledField}
                error={error?.message}
                openMenuOnFocus={false}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_municipality]: ({required, format}) => {
        const showSelectFormat =
          getIsSelectFieldType(
            formFields?.[GUEST_FORM_NAMES.next_destination_municipality]?.type,
          ) && Boolean(nextDestinationDistrict);

        if (showSelectFormat) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.next_destination_municipality}
                rules={{required}}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <AdaptiveQueriedInfiniteScrollSelect
                    label={getRequiredOrOptionalFieldLabel(
                      isNewLabel
                        ? formFields[GUEST_FORM_NAMES.next_destination_municipality]
                            ?.label
                        : t('next_destination_municipality'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    queryKey={[
                      QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                      nextDestinationDistrict,
                    ]}
                    fetcher={(key: string, page: number, searchQuery: string) =>
                      API.locations.getMunicipalitiesList({
                        key,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                        district: nextDestinationDistrict,
                      })
                    }
                    blockQuery={isDisabledField}
                    disabled={isDisabledField}
                    error={error?.message}
                    openMenuOnFocus={false}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              label={getRequiredOrOptionalFieldLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.next_destination_municipality]?.label
                  : t('next_destination_municipality'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.next_destination_municipality, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              control={control}
              error={errors[GUEST_FORM_NAMES.next_destination_municipality]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.next_destination_municipality)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.residence_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.residence_address, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.residence_address]?.label
                : t('residence_address'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.residence_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            tooltipContent={t('residence_address_tooltip')}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.residence_address)
            }
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.residence_address)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_street]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.residence_street, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.residence_street]?.label
                : t('residence_street'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.residence_street]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.residence_street)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.residence_street)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_house_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.residence_house_number, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.residence_house_number]?.label
                : t('residence_house_number'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.residence_house_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.residence_house_number)
            }
            onBlur={() =>
              virtualKeyboard.handleBlur(GUEST_FORM_NAMES.residence_house_number)
            }
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_postal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.residence_postal_code]?.label
                : t('residence_postal_code'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.residence_postal_code, {
              required,
              validate: validatePostalCode(residenceCountry),
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.residence_postal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.residence_postal_code)
            }
            onBlur={() =>
              virtualKeyboard.handleBlur(GUEST_FORM_NAMES.residence_postal_code)
            }
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.next_destination_address, {
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              required,
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.next_destination_address]?.label
                : t('next_destination_address'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.next_destination_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.next_destination_address)
            }
            onBlur={() =>
              virtualKeyboard.handleBlur(GUEST_FORM_NAMES.next_destination_address)
            }
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.fiscal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.fiscal_code, {
              required,
              pattern: {
                value: PATTERNS.fiscalCode,
                message: t('invalid_fiscal_code'),
              },
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.fiscal_code]?.label
                : t('fiscal_code'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.fiscal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            tooltipContent={t('fiscal_code_tooltip')}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.fiscal_code)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.fiscal_code)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_support_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.document_support_number, {
              required,
              pattern: {
                value: format,
                message: getDocumentSupportNumberInvalidMessage(documentType),
              },
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.document_support_number]?.label
                : t('document_support_number'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.document_support_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() =>
              virtualKeyboard.handleFocus(GUEST_FORM_NAMES.document_support_number)
            }
            onBlur={() =>
              virtualKeyboard.handleBlur(GUEST_FORM_NAMES.document_support_number)
            }
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.visa_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            {...register(GUEST_FORM_NAMES.visa_number, {
              required,
              pattern: {
                value: format,
                message: t('invalid_visa_number.czech'),
              },
            })}
            control={control}
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.visa_number]?.label
                : t('visa_number'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.visa_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.visa_number)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.visa_number)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.kinship_relationship]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.kinship_relationship]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.kinship_relationship}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.kinship_relationship]?.label
                    : t('relationship'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.external_category_id]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.external_category_id]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.external_category_id}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.external_category_id]?.label
                    : t('category'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.invoice_leader_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.invoice_leader_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.invoice_leader_type}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.invoice_leader_type]?.label
                    : t('invoice_type_needed'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.recipient_code_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.recipient_code_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.recipient_code_type}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.recipient_code_type]?.label
                    : t('recipient_code_type'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.recipient_code]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              getRecipientCodeLabel(
                isNewLabel
                  ? formFields[GUEST_FORM_NAMES.recipient_code]?.label
                  : t('recipient_code'),
                recipientCodeType,
              ),
              required,
            )}
            {...register(GUEST_FORM_NAMES.recipient_code, {
              required,
              pattern: {
                value: format,
                message: t('invalid_recipient_code'),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.recipient_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.recipient_code)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.recipient_code)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.vat_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel
                ? formFields[GUEST_FORM_NAMES.vat_number]?.label
                : t('vat_number'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.vat_number, {
              required,
              pattern: {
                value: format,
                message: t('invalid_vat_number'),
              },
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.vat_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.vat_number)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.vat_number)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.chip_card]: ({required}) => (
        <FormFieldWrapper>
          <Input
            label={getRequiredOrOptionalFieldLabel(
              isNewLabel ? formFields[GUEST_FORM_NAMES.chip_card]?.label : t('chip_card'),
              required,
            )}
            {...register(GUEST_FORM_NAMES.chip_card, {
              required,
              validate: validateChipCard,
            })}
            control={control}
            error={errors[GUEST_FORM_NAMES.chip_card]?.message}
            disabled={isDisabledField}
            tooltipContent={t('chip_card_tooltip')}
            autoCorrect="off"
            spellCheck={false}
            autoCapitalize="none"
            onFocus={() => virtualKeyboard.handleFocus(GUEST_FORM_NAMES.chip_card)}
            onBlur={() => virtualKeyboard.handleBlur(GUEST_FORM_NAMES.chip_card)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.mode_of_transport]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.mode_of_transport]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.mode_of_transport}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(
                  isNewLabel
                    ? formFields[GUEST_FORM_NAMES.mode_of_transport]?.label
                    : t('mode_of_transport'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={options}
                error={error?.message}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.mode_of_transport)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
    }),
    [
      guestId,
      getRequiredOrOptionalFieldLabel,
      formFields,
      t,
      register,
      housingCountry,
      policeType,
      control,
      errors,
      isDisabledField,
      handleKeyDown,
      birthPlaceCountry,
      getValues,
      setHasPhone,
      isLeaderForm,
      defaultSecondPhoneDetails?.number,
      isGuestSchemaLoading,
      isPoliceActivated,
      documentType,
      guestGroup,
      reservation?.check_in_date,
      shouldFirstGuestBeAdult,
      guestMustBeAdult,
      residenceCountry,
      residenceProvince,
      arrivedFromDistrict,
      nextDestinationDistrict,
      setValue,
      recipientCodeType,
      virtualKeyboard,
      datepickerRefs,
      countrySelectInputsRegister,
      phoneInputRefs,
      layoutMode,
    ],
  );
};
